import type { MetaDescriptor } from 'react-router';

const ROBOTS_TOKEN_ORDER = ['noindex', 'nofollow', 'follow'];

const isRobotsDescriptor = (
    descriptor: MetaDescriptor,
): descriptor is { name: string; content: string } => {
    return (
        'name' in descriptor &&
        descriptor.name === 'robots' &&
        typeof descriptor.content === 'string'
    );
};

/**
 * Collapses multiple robots meta descriptors into a single descriptor by
 * unioning their directive tokens ('nofollow' wins over 'follow'). The merged
 * descriptor keeps the first robots position; the input array is not mutated.
 */
export const mergeRobotsMeta = (
    metaDescriptors: MetaDescriptor[],
): MetaDescriptor[] => {
    const robotsDescriptors = metaDescriptors.filter(isRobotsDescriptor);
    if (robotsDescriptors.length < 2) {
        return metaDescriptors;
    }

    const tokens = new Set(
        robotsDescriptors.flatMap((descriptor) => {
            return descriptor.content
                .split(',')
                .map((token) => token.trim().toLowerCase())
                .filter(Boolean);
        }),
    );
    if (tokens.has('nofollow')) {
        tokens.delete('follow');
    }

    const orderedTokens = [
        ...ROBOTS_TOKEN_ORDER.filter((token) => tokens.has(token)),
        ...[...tokens].filter((token) => !ROBOTS_TOKEN_ORDER.includes(token)),
    ];
    const mergedRobots = { name: 'robots', content: orderedTokens.join(', ') };

    let isFirstRobots = true;
    return metaDescriptors.flatMap((descriptor) => {
        if (!isRobotsDescriptor(descriptor)) {
            return [descriptor];
        }
        if (!isFirstRobots) {
            return [];
        }
        isFirstRobots = false;
        return [mergedRobots];
    });
};
